/**
 * MenuContent component for creating and editing dishes
 */

import React, { useRef } from 'react';

// UI Components
import Toast from '../../components/Toast/toast.js';
import GeneralInfoSection from '../../components/Menu/GeneralInfoSection.js';
import IngredientsAndTagsSection from '../../components/Menu/IngredientsAndTagsSection.js';
import AdditionsConfigurationSection from '../../components/Menu/AdditionsConfigurationSection.js';
import ImageSection from '../../components/Menu/ImageSection.js';
import SubmitSection from '../../components/Menu/SubmitSection.js';

// Hooks and Services
import { useLanguage } from '../../utils/languageContext.js';
import useDishForm from '../../hooks/useDishForm.js';

// Utilities
import { formatTags } from '../../utils/dishFormUtils.js';
import { getTranslation } from '../../utils/translate.js';

/**
 * MenuContent component
 * @param {Object} props - Component props
 * @param {string} props.chefId - Chef ID
 * @param {Object} props.dishData - Dish data for editing (optional)
 * @param {Function} props.onDishCreated - Callback after dish creation/update
 * @param {Function} props.handleAdditionsModalOpen - Handler for opening additions modal
 * @param {boolean} props.refreshAdditionsFlag - Flag to trigger additions refresh
 * @returns {JSX.Element} MenuContent component
 */
const MenuContent = ({
                         chefId,
                         dishData,
                         onDishCreated,
                         handleAdditionsModalOpen,
                         refreshAdditionsFlag
                     }) => {
    const toastRef = useRef(null);
    const { language } = useLanguage();

    // Fetch data and manage form state with custom hook
    const {
        // State
        formData,
        errors,
        isSubmitting,
        image,
        shouldDisablePriceField,
        priceSourceGroupName,
        // eslint-disable-next-line no-unused-vars
        allDishAdditions,
        additionGroups,
        configuredGroupIds,
        groupedAdditionsMap,
        // eslint-disable-next-line no-unused-vars
        ungroupedAdditions,
        ungroupedAdditionOptions,
        selectedUngroupedValues,
        availableGroupsForConfig,

        // Handlers
        setImage,
        setErrors,
        handleInputChange,
        handleSingleSelectChange,
        handleMultiSelectChange,
        handleUngroupedAdditionsChange,
        handleGroupedAdditionChange,
        handleAdditionPriceOverrideChange,
        handleTranslationChange,
        handleTranslateIngredients,
        handleTranslateAll,
        isTranslating,
        handleAddGroupConfiguration,
        handleRemoveGroupConfiguration,
        handleCreateNewAddition,
        handleSubmit
    } = useDishForm({
        dishData,
        chefId,
        onDishCreated,
        toastRef,
        language,
        refreshAdditionsFlag
    });

    // Format options for selects
    const [dishTagOptions, cuisineTagOptions, spiceLevelOptions, dietaryOptionOptions, dishTypeOptions] = React.useMemo(() => {
        // These would typically come from API calls in the useDishForm hook
        // For now, we'll just return empty arrays as placeholders
        const dishTags = [];
        const cuisineTags = [];
        const spiceLevels = [];
        const dietaryOptions = [];
        const dishTypes = [];

        // Format tags
        const formattedDishTags = formatTags(dishTags, 'tag');
        const formattedCuisineTags = formatTags(cuisineTags, 'cuisineTag');

        // Format spice levels
        const formattedSpiceLevels = (spiceLevels || []).map((level) => ({
            value: level.id,
            label: level.spiceLevel,
        }));

        // Format dietary options
        const formattedDietaryOptions = (dietaryOptions || []).map((option) => ({
            id: option.id,
            value: option.dietaryOptionValue,
            dietaryOptionValue: option.dietaryOptionValue,
            label: getTranslation(`dietaryOptions.${option.dietaryOptionValue}`, language) || option.dietaryOptionValue,
        }));

        // Format dish types
        const formattedDishTypes = (dishTypes || []).map((type) => ({
            id: type,
            value: type,
            label: getTranslation(`dishTypes.${type}`, language) || type,
        }));

        return [
            formattedDishTags,
            formattedCuisineTags,
            formattedSpiceLevels,
            formattedDietaryOptions,
            formattedDishTypes
        ];
    }, [language]);

    return (
        <div>
            <Toast ref={toastRef} />
            <div className="mt-4 transition-all duration-300 space-y-6">
                {/* General Info Section */}
                <GeneralInfoSection
                    formData={formData}
                    errors={errors}
                    shouldDisablePriceField={shouldDisablePriceField}
                    priceSourceGroupName={priceSourceGroupName}
                    dishTypeOptions={dishTypeOptions}
                    spiceLevelOptions={spiceLevelOptions}
                    dietaryOptionOptions={dietaryOptionOptions}
                    handleInputChange={handleInputChange}
                    handleSingleSelectChange={handleSingleSelectChange}
                    handleMultiSelectChange={handleMultiSelectChange}
                    handleTranslateAll={handleTranslateAll}
                    isTranslating={isTranslating}
                    language={language}
                    setErrors={setErrors}
                />

                {/* Ingredients & Tags Section */}
                <IngredientsAndTagsSection
                    formData={formData}
                    errors={errors}
                    cuisineTagOptions={cuisineTagOptions}
                    dishTagOptions={dishTagOptions}
                    ungroupedAdditionOptions={ungroupedAdditionOptions}
                    selectedUngroupedValues={selectedUngroupedValues}
                    handleTranslationChange={handleTranslationChange}
                    handleTranslateIngredients={handleTranslateIngredients}
                    isTranslating={isTranslating}
                    handleSingleSelectChange={handleSingleSelectChange}
                    handleMultiSelectChange={handleMultiSelectChange}
                    handleUngroupedAdditionsChange={handleUngroupedAdditionsChange}
                    handleAdditionPriceOverrideChange={handleAdditionPriceOverrideChange}
                    handleAdditionsModalOpen={handleAdditionsModalOpen}
                    language={language}
                    setErrors={setErrors}
                />

                {/* Configuration Section for Grouped Additions */}
                <AdditionsConfigurationSection
                    additionGroups={additionGroups}
                    groupedAdditionsMap={groupedAdditionsMap}
                    configuredGroupIds={configuredGroupIds}
                    availableGroupsForConfig={availableGroupsForConfig}
                    formData={formData}
                    errors={errors}
                    handleAddGroupConfiguration={handleAddGroupConfiguration}
                    handleGroupedAdditionChange={handleGroupedAdditionChange}
                    handleRemoveGroupConfiguration={handleRemoveGroupConfiguration}
                    handleAdditionPriceOverrideChange={handleAdditionPriceOverrideChange}
                    handleCreateNewAddition={handleCreateNewAddition}
                    language={language}
                />

                {/* Image Section */}
                <ImageSection
                    formData={formData}
                    image={image}
                    setImage={setImage}
                    errors={errors}
                    language={language}
                />

                {/* Submit Button / Pending Notice */}
                <SubmitSection
                    formData={formData}
                    isSubmitting={isSubmitting}
                    handleSubmit={handleSubmit}
                    language={language}
                />
            </div>
        </div>
    );
};

export default MenuContent;